import { DestroyRef, ElementRef, Injectable } from '@angular/core';
import {
  API,
  BookingDTO,
  CommercialDTO,
  CommercialFullDTO,
  CullDTO,
  MediumDTO,
  MediumGroupFullDTO,
  NamedIdentityDTO,
  PublicMediumDTO,
  RequirementFullDTO,
  ScheduledCampaignDTO,
  ScheduledCommercialDTO,
} from '../api/api';
import {
  CollectionSorter,
  DateHelper,
  EventModel,
  EventStore,
  Popup,
  ResourceStore,
  Scheduler,
} from '@bryntum/scheduler/scheduler.module.js';
import { UtilService } from '../util.service';
import { ErrorService } from '../error.service';
import { BookingService } from '../booking.service';
import { SchedulerComponent } from '../../components/booking-editor/scheduler-wrapper/scheduler.component';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService, ToastType } from '../toaster/toaster.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, switchMap, take } from 'rxjs/operators';
import { forkJoin, from, of, throwError } from 'rxjs';
import { Mutable } from '../global/global-interfaces';
import { SbLocalDateTime } from '../api/api-date-time';
import { DateTime } from 'luxon';

interface BookingStatusData {
  bookingMessage: string;
  commercialData: EventModel;
}

interface BookingStatusWrapper {
  corruptedData: BookingStatusData[];
  toConsider: BookingStatusData[];
}

type EditingRecord = EventModel & {
  data: any;
};

@Injectable({
  providedIn: 'root',
})
export class BookingEditorService {
  readonly: boolean;
  scheduler: SchedulerComponent;
  editorType: string;
  editingRecord: EditingRecord;
  targetElement: ElementRef;
  bookingEditorCampaigns = [];
  bookingEditorData: any;
  loading: boolean = true;
  startDate: Date;
  leftPos: number;
  popup: Popup;
  planningId: number;
  phantomEventElement: any;
  rawData: ScheduledCampaignDTO[];
  sortedColumn: string;
  corruptedCommercials: BookingStatusWrapper = {
    corruptedData: [],
    toConsider: [],
  };
  customer: NamedIdentityDTO;

  resourceStore: ResourceStore;
  eventStore: EventStore;

  constructor(
    private api: API,
    private util: UtilService,
    private errorHandler: ErrorService,
    private bookingService: BookingService,
    private translate: TranslateService,
    private toastService: ToasterService,
    private destroyRef: DestroyRef
  ) {
    this.corruptedCommercials = {
      corruptedData: [],
      toConsider: [],
    };
    this.loading = true;
    this.customer = null;
  }

  checkPlanning() {
    return new Promise(resolve => {
      this.corruptedCommercials = {
        corruptedData: [],
        toConsider: [],
      };
      this.eventStore.forEachScheduledEvent(this.checkForCorruptedCommercials, this);
      if (this.corruptedCommercials.corruptedData.length > 0) {
        resolve(false);
      } else {
        resolve(true);
      }
    });
  }

  sortInForCorruptedCommercials(commercial: ScheduledCommercialDTO, data: BookingStatusData) {
    if (this.corruptedCommercials) {
      if (commercial.overwrites) {
        this.corruptedCommercials.toConsider.push(data);
      }
      if (!commercial.overwrites) {
        this.corruptedCommercials.corruptedData.push(data);
      }
    }
  }

  // TODO two versions of check, info check and conflict check
  checkForCorruptedCommercials(record: EditingRecord) {
    if (record?.data?.commercial?.planning) {
      const bookingStatus = record.data.commercial.bookingStatus;
      const bookingStatusData = {
        bookingMessage: '',
        commercialData: record,
      };
      if (!bookingStatus || Object.keys(bookingStatus).length === 0) {
        bookingStatusData.bookingMessage = this.translate.instant('lang.actionRequired.reasons.noBookings');
        this.sortInForCorruptedCommercials(record.data.commercial, bookingStatusData);
        return;
      }

      if (bookingStatus.Rejected) {
        const rejCount = bookingStatus.Rejected;
        bookingStatusData.bookingMessage = this.translate.instant('lang.actionRequired.reasons.rejectedVerificationCount', {
          count: rejCount,
        });
        this.sortInForCorruptedCommercials(record.data.commercial, bookingStatusData);
      }

      if (bookingStatus.VerificationRevoked) {
        const revCount = bookingStatus.VerificationRevoked;
        bookingStatusData.bookingMessage = this.translate.instant('lang.actionRequired.reasons.revokedVerificationCount', {
          count: revCount,
        });
        this.sortInForCorruptedCommercials(record.data.commercial, bookingStatusData);
      }

      if (bookingStatus.Proposed) {
        const propCount = bookingStatus.Proposed;
        bookingStatusData.bookingMessage = this.translate.instant('lang.actionRequired.reasons.motivesMissingCount', { count: propCount });
        this.sortInForCorruptedCommercials(record.data.commercial, bookingStatusData);
      }

      if (bookingStatus.VerificationNotRequested) {
        const vnrCount = bookingStatus.VerificationNotRequested;
        bookingStatusData.bookingMessage = this.translate.instant('lang.actionRequired.reasons.verificationRequestsMissingCount', {
          count: vnrCount,
        });
        this.sortInForCorruptedCommercials(record.data.commercial, bookingStatusData);
      }
    }
  }

  async saveCommercialEdit(tempSave?: boolean, restoreCommercial?: boolean) {
    let now = new Date();
    const eventRecord = this.eventStore.findRecord('id', this.editingRecord.id);
    if (!eventRecord && !tempSave) {
      this.phantomEventElement = null;
      await this.eventStore.addAsync(this.editingRecord); // async to guarentee updated data
    }
    this.editingRecord.setAsync('name', this.editingRecord.data.commercial.name);
    this.editingRecord.setStartDate(this.editingRecord.data.commercial.from.value.toJSDate(), false);
    this.editingRecord.setEndDate(this.editingRecord.data.commercial.till.value.toJSDate(), false);
    this.editingRecord.setAsync('commercialDuration', this.editingRecord.data.commercial.duration);
    this.editingRecord.setAsync('sequence', this.editingRecord.data.commercial.sequence);
    this.editingRecord.setAsync('borderColor', this.editingRecord.data.commercial.color);
    if (!tempSave) {
      if (
        this.editingRecord.get('startDate') < now &&
        this.editingRecord.get('commercial').planning &&
        !this.editingRecord.get('commercial').overwrites
      ) {
        now.setHours(0, 0, 0, 0);
        now = DateHelper.add(now, 1, 'day');
        this.editingRecord.setStartDate(now, false);
      }
      if (!restoreCommercial) {
        this.afterEventSave(this.editingRecord, true);
      } else {
        this.editingRecord.setAsync('planning', this.editingRecord.data.commercial.planning);
      }
      if (this.popup) {
        this.popup.close();
        this.popup.destroy();
      }
    }
  }

  cancelCommercialEdit() {
    this.editingRecord = null;
    this.targetElement = null;
    this.removePhantomEventElement();
    if (this.popup) {
      this.popup.close();
      this.popup.destroy();
    }
  }

  removePhantomEventElement() {
    if (this.phantomEventElement && !this.phantomEventElement.id) {
      this.phantomEventElement?.remove();
      this.phantomEventElement = null;
    }
  }

  findOverwriteCommercial(commercialIdToFind: number): CommercialDTO {
    for (const campaign of this.rawData) {
      for (const commercial of campaign.commercials) {
        if (commercial.id === commercialIdToFind) {
          return commercial as unknown as CommercialDTO; // todo check here
        }
      }
    }
  }

  showEditor(switchMode?: boolean) {
    if (switchMode === false && switchMode != null) {
      // not null and false
      this.removePhantomEventElement();
    }
    if (this.popup?.close) {
      this.popup.close();
    }
    if (this.popup?.destroy) {
      this.popup.destroy();
    }
    if (this.editorType === 'smallEditor' || !this.editorType) {
      this.showSmallEditor();
    } else {
      this.showBigEditor();
    }
  }

  showSmallEditor() {
    this.popup = new Popup({
      align: 'b-t',
      scrollAction: 'realign',
      floating: true,
      anchor: true,
      autoClose: false,
      autoShow: false,
      cls: 'card border-radius-popup',
      width: '30rem',
      maxWidth: '30rem',
      constrainTo: document.body,
      // @ts-ignore
      clippedBy: [this.scheduler.schedulerInstance.timeAxisSubGridElement, this.scheduler.schedulerInstance['bodyElement']],
      html: `<sb-commercial-editor></sb-commercial-editor>`,
    });
    this.popup
      .showBy({
        target: this.phantomEventElement ? this.phantomEventElement : this.targetElement,
      })
      .then();
  }

  showBigEditor() {
    this.popup = new Popup({
      autoShow: false,
      centered: true,
      width: '100rem',
      maxWidth: '100%',
      cls: 'card d-block',
      // @ts-ignore
      modal: {
        closeOnMaskTap: false,
      },
      html: `<sb-commercial-editor style="width: 100rem; display: block;"></sb-commercial-editor>`,
    });
    this.popup.show().then();
  }

  editCommercialData(eventRecord: any, resource: any): CommercialDTO {
    let commercialData = eventRecord.get('commercial');
    if (!commercialData) {
      commercialData = new CommercialDTO();
    }
    commercialData.name = eventRecord.get('name');
    commercialData.from = new SbLocalDateTime(DateTime.fromJSDate(eventRecord.get('startDate')));
    commercialData.till = new SbLocalDateTime(DateTime.fromJSDate(eventRecord.get('endDate')));
    commercialData.color = eventRecord.data.commercial.color
      ? eventRecord.data.commercial.color
      : this.util.renderStringColor(eventRecord.get('name'));
    commercialData.duration = eventRecord.get('commercialDuration');
    commercialData.timeCull = resource.data.timeCull;
    commercialData.playerCull = resource.data.playerCull;
    commercialData.geoCull = resource.data.geoCull;
    return commercialData;
  }

  setCommercialData(eventRecord: EventModel, data: CommercialDTO) {
    eventRecord.set('commercial', data);
    eventRecord.set('planning', data.planning);
  }

  afterEventSave(event: any, valid: boolean, isCopied?: boolean) {
    if (!valid) {
      return;
    }
    let eventRecord: any;
    if (event.eventRecord || event?.eventRecords) {
      eventRecord = event.eventRecord ? event.eventRecord : event?.eventRecords[0];
    } else {
      eventRecord = event;
    }
    eventRecord.set('loading', true);
    const resource = eventRecord.getResource(eventRecord.resourceId);
    eventRecord.set('commercial', this.editCommercialData(eventRecord, resource));
    eventRecord.set(
      'borderColor',
      eventRecord.data.commercial.color ? eventRecord.data.commercial.color : this.util.renderStringColor(eventRecord.get('name'))
    );
    if (eventRecord.data.commercial.id) {
      // edit
      if (eventRecord.data.commercial.planning) {
        // in planning
        this.editInPlanning(eventRecord);
      } else {
        // overwrite
        this.overwriteInPlanning(eventRecord);
      }
    } else {
      // create
      this.createInPlanning(eventRecord, isCopied ?? false);
    }
  }

  calcDuration(mediumGroup: MediumGroupFullDTO) {
    let duration: number = 10;
    if (mediumGroup) {
      for (const medi of mediumGroup.media) {
        if (medi.duration) {
          duration = medi.duration;
        }
      }
    }
    return duration;
  }

  checkForOverrideCommercials(commercials: ScheduledCommercialDTO[]) {
    const newData: ScheduledCommercialDTO[] = [];
    for (const commercial of commercials) {
      if (commercial.planning && commercial.overwrites) {
        const overwriteId = commercial.overwrites.id;
        const index = commercials.findIndex(x => x.id === overwriteId);
        if (index !== -1) {
          const toOverwrite = commercials[index];
          this.assignCommercialData(toOverwrite, commercial);
        } else {
          console.log('dropped unresolvable');
        }
      } else {
        newData.push(commercial);
      }
    }
    return newData;
  }

  assignCommercialData(element: ScheduledCommercialDTO, overwrite: ScheduledCommercialDTO) {
    const excludes = ['requirements'];
    for (const attr in overwrite) {
      if (attr) {
        const index = excludes.findIndex(x => x === attr);
        if (index === -1) {
          element[attr] = overwrite[attr];
        }
      }
    }
  }

  checkForOverrideCampaigns(data: ScheduledCampaignDTO[]) {
    const newData: ScheduledCampaignDTO[] = [];
    for (const campaign of data) {
      campaign.commercials = this.checkForOverrideCommercials(campaign.commercials);
      newData.push(campaign);
    }
    return newData;
  }

  getData(scheduler: Scheduler, startDate: Date, endDate: Date, planningId?: number, submitted?: number) {
    return new Promise(resolve => {
      this.corruptedCommercials = {
        corruptedData: [],
        toConsider: [],
      };
      this.loading = true;
      this.startDate = startDate;
      const SbStartDate = new SbLocalDateTime(DateTime.fromJSDate(startDate));
      const SbEndDate = new SbLocalDateTime(DateTime.fromJSDate(endDate));
      this.bookingEditorData = {
        bookingEditorCampaigns: [],
        bookingEditorCommercials: [],
        timeRanges: [],
      };

      this.api
        .ds_cm_ScheduledCampaign_retrieveAll({
          from: SbStartDate,
          till: SbEndDate,
          dropRequirements: true,
          planningSubmission: submitted >= 0 ? submitted : null,
          planning: planningId ?? null,
        })
        .pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {})
        )
        .subscribe({
          next: data => {
            this.rawData = data;

            data = this.checkForOverrideCampaigns(data);
            this.buildCampaignResources(data).then((r: any) => {
              r.bookingEditorCampaigns.forEach((resource: any) => {
                this.resourceStore.add(resource);
              });

              r.bookingEditorCommercials.forEach((resource: any) => {
                this.eventStore.add(resource);
              });

              r.timeRanges.forEach((range: any) => {
                scheduler.resourceTimeRangeStore.add(range);
              });
              this.sortCampaigns('Basic', true);
              this.checkPlanning().then();
              resolve(true);
              this.loading = false;
            });
          },
          error: error => {
            this.errorHandler.errorHandler(error);
            this.loading = false;
          },
        });
    });
  }

  sortCampaigns(column: string, initial?: boolean) {
    const sorters: CollectionSorter = this.resourceStore.sorters;
    let ascending = true;
    if (sorters[0] && !initial) {
      sorters[0].ascending = !sorters[0].ascending;
      ascending = sorters[0].ascending;
    }
    switch (column) {
      case 'Basic': {
        this.resourceStore.sort((recordA, recordB) => {
          if (recordA.isParent && recordB.isParent) {
            return recordA.data.campaign.clientOrderNum - recordB.data.campaign.clientOrderNum;
          }
        }, ascending);
        break;
      }
      case 'Name': {
        this.resourceStore.sort((recordA, recordB) => {
          if (recordA.isParent && recordB.isParent) {
            return recordA.name - recordB.name;
          }
        }, ascending);
        break;
      }
      case 'Level': {
        this.resourceStore.sort((recordA, recordB) => {
          if (recordA.isParent && recordB.isParent) {
            return recordA.data.campaign.level.id.level - recordB.data.campaign.level.id.level;
          }
        }, ascending);
        break;
      }
      default: {
        break;
      }
    }
    this.sortedColumn = column;
  }

  /* START BUILD DATA */

  buildCampaignResources(campaigns: ScheduledCampaignDTO[]) {
    return new Promise(resolve => {
      let commercialRowId = 1;
      for (const currentCampaign of campaigns) {
        const campaignObj = {
          id: commercialRowId,
          expanded: true,
          assignable: false,
          children: [],
          campaign: currentCampaign,
          name: currentCampaign.name,
          orderNumber: currentCampaign.clientOrderNum,
        };
        this.buildShadowEvent(commercialRowId, currentCampaign.commercials);
        for (const commercial of currentCampaign.commercials) {
          let possibleRowId = this.findSameCullSettings(campaignObj.children, commercial);
          if (!possibleRowId) {
            commercialRowId++;
            possibleRowId = commercialRowId;
            campaignObj.children.push(this.buildCommercialRow(currentCampaign, commercial, commercialRowId));
            this.bookingEditorData.timeRanges.push(this.buildTimeRange(commercialRowId));
          }
          this.buildEvent(commercial, possibleRowId);
        }

        campaignObj.children.sort((a, b) => {
          return this.compareCullNames(a.timeCull, b.timeCull);
        });
        if (campaignObj.children.length !== 0) {
          this.bookingEditorData.bookingEditorCampaigns.push(campaignObj);
        }
        commercialRowId++;
      }
      resolve(this.bookingEditorData);
    });
  }

  compareCullNames(cullA: CullDTO, cullB: CullDTO): number {
    if (!cullA) {
      return -1;
    }
    if (!cullB) {
      return 1;
    }
    return this.compareCullValue(this.cullValue(cullA), this.cullValue(cullB));
  }

  compareCullValue(valA: number, valB: number): number {
    if (valA > valB) {
      return -1;
    } else if (valB < valA) {
      return 1;
    } else {
      return 0;
    }
  }

  cullValue(cull: CullDTO): number {
    if (cull.name === '4-10 Uhr') {
      return 4;
    }
    if (cull.name === '10-16 Uhr') {
      return 3;
    }
    if (cull.name === '16-22 Uhr') {
      return 2;
    }
    if (cull.name === '22-4 Uhr') {
      return 1;
    }
    return 0;
  }

  findSameCullSettings(commercialRows: any[], commercial: ScheduledCommercialDTO) {
    if (!commercialRows || commercialRows?.length <= 0) {
      return null;
    }
    for (const commercialRow of commercialRows) {
      if (
        this.equalCull(commercialRow.timeCull, commercial.timeCull) &&
        this.equalCull(commercialRow.playerCull, commercial.playerCull) &&
        this.equalCull(commercialRow.geoCull, commercial.geoCull)
      ) {
        return commercialRow.id;
      }
    }
    return null;
  }

  equalCull(cullA, cullB) {
    return (!cullA && !cullB) || (cullA && cullB && cullA.id === cullB.id);
  }

  buildCommercialRow(campaign: ScheduledCampaignDTO, commercial: ScheduledCommercialDTO, commercialRowId: number) {
    return {
      name:
        campaign.name +
        ' || ' +
        (commercial.timeCull ? commercial.timeCull.name : '') +
        ' | ' +
        (commercial.playerCull ? commercial.playerCull.name : '') +
        ' | ' +
        (commercial.geoCull ? commercial.geoCull.name : ''),
      id: commercialRowId,
      assignable: true,
      timeCull: commercial.timeCull,
      playerCull: commercial.playerCull,
      geoCull: commercial.geoCull,
    };
  }

  buildTimeRange(commercialRowId: number) {
    const now = new Date();
    return {
      name: 'Past',
      resourceId: commercialRowId,
      startDate: this.startDate,
      endDate: now,
      cls: 'commercialRowPast',
    };
  }

  buildEvent(pCommercial: ScheduledCommercialDTO, commercialRowId: number, singleBuild?: boolean) {
    const commercialObj = {
      name: pCommercial.name,
      resourceId: commercialRowId,
      startDate: pCommercial.from.value.toJSDate(),
      endDate: pCommercial.till.value.toJSDate(),
      commercialDuration: 10,
      sequence: 0,
      commercial: pCommercial,
      cls: 'commercialEvent',
      borderColor: pCommercial?.color ? pCommercial.color : this.util.renderStringColor(pCommercial.name),
      planning: pCommercial.planning,
      loading: false,
      resizable: this.verifyResizable(pCommercial),
      readonly: !this.verifyResizable(pCommercial),
      previews: pCommercial.preview,
    };
    if (singleBuild) {
      return commercialObj;
    } else {
      this.bookingEditorData.bookingEditorCommercials.push(commercialObj);
    }
  }

  verifyResizable(commercial: ScheduledCommercialDTO): boolean {
    let now = new Date();
    now.setHours(0, 0, 0, 0);
    now = DateHelper.add(now, 1, 'day');
    if (commercial.from.value.toJSDate() < now && commercial.till.value.toJSDate() <= now) {
      return commercial.planning;
    } else {
      return true;
    }
  }

  buildPreviewMedias(commercial: ScheduledCommercialDTO): PublicMediumDTO[] {
    const previews: PublicMediumDTO[] = [];
    if (commercial?.requirements?.length > 0) {
      for (const req of commercial.requirements) {
        if (req.previewMedia.length > 0) {
          previews.push(req.previewMedia[0]);
        }
      }
    }
    return previews;
  }

  buildShadowEvent(commercialRowId, commercials) {
    const dates = [];
    for (const commercial of commercials) {
      dates.push(commercial.from.value);
      dates.push(commercial.till.value);
    }
    const varStartDate = new Date(Math.min.apply(null, dates));
    varStartDate.setMonth(varStartDate.getMonth() + 1);
    const varEndDate = new Date(Math.max.apply(null, dates));
    varEndDate.setMonth(varEndDate.getMonth() + 1);
    const shadowObj = {
      resourceId: commercialRowId,
      startDate: varStartDate,
      endDate: varEndDate,
      cls: 'campaignEvent',
      eventColor: 'orange',
    };
    this.bookingEditorData.bookingEditorCommercials.push(shadowObj);
  }

  /*END BUILD DATA */
  private editInPlanning(eventRecord: any) {
    forkJoin({
      commercial: this.api.ds_cm_Commercial_editCommercialPlanning(this.planningId, eventRecord.data.commercial),
      requirement: this.api.ds_cm_Requirement_retrieveByIdInPlanning(eventRecord.data.commercial.requirements[0].id, this.planningId),
      bookings: this.api.ds_cm_Requirement_retrieveBookingsInPlanning(eventRecord.data.commercial.requirements[0].id, this.planningId),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          eventRecord.set('loading', false);
        })
      )
      .subscribe({
        next: ({ commercial, requirement, bookings }) => {
          (requirement as RequirementFullDTO).bookings = bookings;
          (commercial as CommercialFullDTO).requirements = [requirement as RequirementFullDTO];
          eventRecord.data.commercial = commercial;
          eventRecord.data.previews = this.buildPreviewMedias(eventRecord.data.commercial);
          this.checkPlanning().then();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  private overwriteInPlanning(eventRecord: any) {
    const commercialId = eventRecord.data.commercial.id;
    eventRecord.data.commercial.id = null;
    forkJoin({
      commercial: this.api.ds_cm_Campaign_createCommercialPlanningOverwrite(this.planningId, commercialId, eventRecord.data.commercial),
      requirement: this.api.ds_cm_Requirement_retrieveByIdInPlanning(eventRecord.data.commercial.requirements[0].id, this.planningId),
      bookings: this.api.ds_cm_Requirement_retrieveBookingsInPlanning(eventRecord.data.commercial.requirements[0].id, this.planningId),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          eventRecord.set('loading', false);
        })
      )
      .subscribe({
        next: ({ commercial, requirement, bookings }) => {
          (requirement as RequirementFullDTO).bookings = bookings;
          commercial.requirements = [requirement as RequirementFullDTO];
          eventRecord.data.commercial = commercial;
          eventRecord.data.previews = this.buildPreviewMedias(eventRecord.data.commercial);
          eventRecord.set('planning', true);
          this.checkPlanning().then();
          this.toastService.toast(
            ToastType.SUCCESS,
            this.translate.instant(`lang.bookingEditor.successfullyCreated`, { commercial: eventRecord.name })
          );
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  private createInPlanning(eventRecord: any, isCopied?: boolean) {
    const resource = eventRecord.getResource(eventRecord.resourceId);
    const campaign = resource.parent.get('campaign');
    let commercial: CommercialFullDTO = eventRecord.data?.commercial;
    let requirements: RequirementFullDTO[] = commercial.requirements;
    this.api
      .ds_cm_Campaign_createCommercialPlanning(campaign.id, this.planningId, eventRecord.data.commercial)
      .pipe(
        switchMap(data => {
          if (commercial.requirements?.length > 1) {
            requirements = [...commercial.requirements];
          }

          this.setCommercialData(eventRecord, data);
          commercial = eventRecord.data.commercial;
          return from(this.bookingService.createRequirements(commercial, this.planningId, requirements));
        }),
        switchMap((createdRequirements: Mutable<RequirementFullDTO>[]) => {
          if (createdRequirements) {
            commercial.requirements = [...createdRequirements];
          }
          return from(this.bookingService.createBookings(commercial.requirements, this.planningId));
        }),
        switchMap((createdBookings: Map<number, BookingDTO[]>) => {
          if (isCopied) {
            // check if requirement count from original and copy match
            if (!requirements || createdBookings?.size !== requirements?.length) {
              throwError(new Error('Not matching requirement count'));
            }
            createdBookings.forEach((bookings, index) => {
              commercial.requirements[index].bookings = [...bookings];
              // set Media to bookings
              const previewMediaSet: Set<MediumDTO> = new Set();
              requirements[index].bookings.forEach(reqBooking => {
                const booking = commercial.requirements[index].bookings.find(
                  lookUpBooking => lookUpBooking.channel?.id === reqBooking.channel?.id
                );
                booking.excluded = reqBooking.excluded;
                if (reqBooking.medium) {
                  booking.medium = {
                    ...reqBooking.medium,
                  };
                  previewMediaSet.add({ ...reqBooking.medium });
                }
              });
              // Mutable cast for editable since previewMedia is readonly
              (commercial.requirements[index] as Mutable<RequirementFullDTO>).previewMedia = Array.from(previewMediaSet.values());
            });
            const editedBookingPromises = commercial.requirements.map((requirement: RequirementFullDTO) => {
              return from(this.bookingService.editBookings(requirement.bookings, this.planningId));
            });
            return forkJoin(editedBookingPromises).pipe(
              switchMap(editedBookings => {
                editedBookings.forEach((editBookings, index) => {
                  commercial.requirements[index].bookings = [...editBookings];
                });
                return of(commercial);
              })
            );
          } else {
            return of(commercial);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          eventRecord.set('loading', false);
        })
      )
      .subscribe({
        next: (createdCommercial: CommercialFullDTO) => {
          eventRecord.data.commercial = { ...createdCommercial };
          eventRecord.data.previews = this.buildPreviewMedias(eventRecord.data.commercial);
          this.toastService.toast(
            ToastType.SUCCESS,
            this.translate.instant(`lang.bookingEditor.successfullyCreated`, { commercial: eventRecord.name })
          );
          this.checkPlanning().then();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }
}
